// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, ASSET_TYPES } from '../../lib/eventLog.js';

export function CashFlow() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;

  const now = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));

  const { inflows, outflows } = useMemo(() => {
    const inflowMap = new Map<string, number>();
    const outflowMap = new Map<string, number>();

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      if (tx.date < dateFrom || tx.date > dateTo) continue;

      for (const split of tx.splits) {
        const account = ledger.accounts.get(split.accountId);
        if (!account) continue;
        if (!ASSET_TYPES.has(account.type)) continue;

        if (split.amount > 0) {
          inflowMap.set(account.id, (inflowMap.get(account.id) ?? 0) + split.amount);
        } else if (split.amount < 0) {
          outflowMap.set(account.id, (outflowMap.get(account.id) ?? 0) + split.amount);
        }
      }
    }

    const inflows = [...inflowMap.entries()]
      .map(([id, amount]) => ({ name: ledger.accounts.get(id)?.name ?? id, amount }))
      .filter((r) => r.amount !== 0)
      .sort((a, b) => b.amount - a.amount);

    const outflows = [...outflowMap.entries()]
      .map(([id, amount]) => ({ name: ledger.accounts.get(id)?.name ?? id, amount }))
      .filter((r) => r.amount !== 0)
      .sort((a, b) => a.amount - b.amount);

    return { inflows, outflows };
  }, [ledger.transactions, ledger.accounts, dateFrom, dateTo]);

  const totalIn = inflows.reduce((sum, r) => sum + r.amount, 0);
  const totalOut = outflows.reduce((sum, r) => sum + r.amount, 0);
  const netCashFlow = totalIn + totalOut;

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.cashFlow')}</h2>
        <div className="flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.from')}</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.to')}</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <button onClick={() => window.print()}
            className="px-3 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors no-print">
            {t('reports:controls.print')}
          </button>
        </div>
      </div>

      <div className="bg-bg-surface text-text border border-border max-w-xl print:bg-white print:text-gray-900 print:border-gray-200 print-full">
        <div className="px-4 py-3 border-b border-border print:border-gray-200">
          <h3 className="text-base font-bold text-text print:text-gray-900">{t('reports:titles.cashFlowStatement')}</h3>
          <p className="text-xs text-text-muted mt-0.5 print:text-gray-500">{t('reports:controls.dateRange', { from: dateFrom, to: dateTo })}</p>
        </div>

        {/* Inflows */}
        <div>
          <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
            <h4 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{t('reports:sections.cashInflows')}</h4>
          </div>
          <table className="w-full">
            <tbody>
              {inflows.length === 0 && (
                <tr><td colSpan={2} className="py-3 text-center text-sm text-text-subtle print:text-gray-400">{t('common:emptyState.none')}</td></tr>
              )}
              {inflows.map((r) => (
                <tr key={r.name} className="border-b border-border print:border-gray-100">
                  <td className="py-1.5 px-4 text-sm text-text-muted print:text-gray-700">{r.name}</td>
                  <td className="py-1.5 px-4 text-right text-sm font-mono text-positive print:text-green-700">{formatAmount(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-border-strong print:border-gray-300">
              <tr>
                <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.totalIn')}</td>
                <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-positive print:text-green-700">{formatAmount(totalIn)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Outflows */}
        <div className="border-t border-border print:border-gray-200">
          <div className="px-4 py-1.5 border-b border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
            <h4 className="text-sm font-semibold text-text-muted uppercase tracking-wide print:text-gray-700">{t('reports:sections.cashOutflows')}</h4>
          </div>
          <table className="w-full">
            <tbody>
              {outflows.length === 0 && (
                <tr><td colSpan={2} className="py-3 text-center text-sm text-text-subtle print:text-gray-400">{t('common:emptyState.none')}</td></tr>
              )}
              {outflows.map((r) => (
                <tr key={r.name} className="border-b border-border print:border-gray-100">
                  <td className="py-1.5 px-4 text-sm text-text-muted print:text-gray-700">{r.name}</td>
                  <td className="py-1.5 px-4 text-right text-sm font-mono text-negative print:text-red-700">{formatAmount(r.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-border-strong print:border-gray-300">
              <tr>
                <td className="py-1.5 px-4 text-sm font-semibold text-text-muted print:text-gray-700">{t('reports:sections.totalOut')}</td>
                <td className="py-1.5 px-4 text-right text-sm font-mono font-semibold text-negative print:text-red-700">{formatAmount(totalOut)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <table className="w-full border-t-2 border-border-strong print:border-gray-300">
          <tbody>
            <tr>
              <td className="py-2 px-4 text-base font-bold text-text print:text-gray-900">{t('reports:sections.netCashFlow')}</td>
              <td className={`py-2 px-4 text-right text-base font-mono font-bold ${netCashFlow >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>
                {formatAmount(netCashFlow)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
